import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';

import { chartColors } from '../../helpers/chartColors';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/userSlice';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { useTheme } from '@mui/styles';

export const RTFOStepdownChart = ({ stepdownData, gridView, showSpikeLines, showLegend, editMode, setStepdownData, range, setRecalculate, sdtType, preSandStepdownData=[], setPreSandStepdownData }) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];

  const stepdownParams = currentStage?.stepdownParams || {};
  
  let firstTimeStamp = 0;
  let lastTimeStamp = 0;
  if (range) {
    firstTimeStamp = range?.firstTimeStamp;
    lastTimeStamp = range?.lastTimeStamp;
  } else {
    firstTimeStamp = Math.min(stepdownParams?.zeroBHProppantTimestamp, stepdownData?.[0]?.timestamp);
    lastTimeStamp = currentStage?.isipData?.isipTimestamp?.endTimestamp || (stepdownData?.[stepdownData?.length - 1]?.timestamp + 60 * 1000);
  };

  const stageData = useMemo(() => {
    if (sdtType === 'flush') {
      return currentStage?.coldboreData?.filter(d => d.timestamp > (firstTimeStamp - 60 * 1000) && d.timestamp < (lastTimeStamp));
    } else if (sdtType === 'preSand') {
      const startIndex = currentStage?.coldboreData?.findIndex(d => d.rate > 1);
      const endIndex = currentStage?.coldboreData?.findLastIndex(d => d.rate < 1 && d.timestamp <= currentStage?.metrics?.preSandTimestamp);
      return currentStage?.coldboreData?.slice(startIndex, endIndex);
    } else {
      return [];
    };
  }, [currentStage?.coldboreData, currentStage?.metrics?.preSandTimestamp, firstTimeStamp, lastTimeStamp, sdtType])

  const data = useMemo(() => {
    if (!stageData || !stageData?.length > 0) return [];
    
    return getChartData();

    function getChartData() {
      try {

        const X = [];
        const XStepdown = [];
        const YPressure = [];
        const YRate = [];
        const YStepdownPressure = [];
        const YStepdownRate = [];

        stageData?.forEach(d => {
          X.push(new Date(d.timestamp));
          YPressure.push(d.pressure);
          YRate.push(d.rate);
        });

        if (sdtType === 'flush' && stepdownData) {
          stepdownData?.forEach(d => {
           XStepdown.push(new Date(d.timestamp));
           YStepdownPressure.push(d.pressure);
           YStepdownRate.push(d.rate);
          });
        } else if (sdtType === 'preSand' && preSandStepdownData) {
          preSandStepdownData?.forEach(d => {
           XStepdown.push(new Date(d.timestamp));
           YStepdownPressure.push(d.pressure);
           YStepdownRate.push(d.rate);
          });
        };

        // add traces
        let treatingPressureTrace = {
          x: X,
          y: YPressure,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Pressure (MPa)',
          line: {
            width: 2,
            color: chartColors[2]
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let rateTrace = {
          x: X,
          y: YRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'Rate (m3/min)',
          line: {
            width: 2,
            color: chartColors[3]
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let stepdownRateTrace = {
          x: XStepdown,
          y: YStepdownRate,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'Stepdown Rate',
          marker: {
            size: 10,
            color: chartColors[3],
            symbol: 'circle-open',
            line: {
              width: 3,
              color: chartColors[3]
            }
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let stepdownPressureTrace = {
          x: XStepdown,
          y: YStepdownPressure,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y1',
          xaxis: 'x1',
          name: 'Stepdown Pressure',
          marker: {
            size: 10,
            color: chartColors[2],
            symbol: 'circle-open',
            line: {
              width: 3,
              color: chartColors[2]
            }
          },
          hoverinfo: !showSpikeLines && 'none'
        };

        return [ 
          treatingPressureTrace, 
          rateTrace,
          stepdownRateTrace,
          stepdownPressureTrace
        ];
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [preSandStepdownData, sdtType, showSpikeLines, stageData, stepdownData]);

  const layout = {
    // grid: {rows: 3, columns: 2, pattern: 'independent'},
    yaxis: { // Treating pressure y axis
      linecolor: chartColors[2],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[2],
      title: {
        text: 'Treating Pressure (MPa)',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      // autorange: true,
      range: [0, 100],
      side: 'left',
      // uirevision: 'true'
    },
    yaxis2: { // Slurry rate y axis
      linecolor: chartColors[3],
      showgrid: false,
      color: chartColors[3],
      title: {
        text: 'Slurry Rate (m3/min)',
        font: {
          size: 12
        },
      },
      range: [0, 25],
      anchor: 'x',
      overlaying: 'y',
      side: 'right'
    },
    xaxis: { // Date x axis
      autorange: true,
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      // domain: [(gridView ? 0.1 : 0.05), (gridView ? 0.9 : 0.95)],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      type: 'date',
    },
    hovermode: "x",
    hoverdistance: 10, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: showLegend ? true : false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      // x: 1,
      // xanchor: 'right',
      // xref: 'paper',
      y: (gridView ? -0.15 : -0.1),
    },
    annotations: [
      {
        text: `${sdtType === 'flush' ? 'Flush' : 'Pre Sand'} Stepdown Analysis`,
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || "No stage selected"}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.09 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      sdtType === 'flush' && stepdownParams?.zeroBHProppantTimestamp && {
        x: new Date(stepdownParams?.zeroBHProppantTimestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Zero BH Proppant`,
        textangle: '-90',
        showarrow: false,
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
      }
    ],
    shapes: [
      sdtType === 'flush' && stepdownParams?.zeroBHProppantTimestamp && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(stepdownParams?.zeroBHProppantTimestamp),
        y0: 0,
        x1: new Date(stepdownParams?.zeroBHProppantTimestamp),
        y1: 1,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 2,
          dash: 'dot'
        }
      }
    ],
    uirevision: 'true',
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (gridView ? 0.05 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  // for each stepdown value, add a vertical line
  const shapes = [];
  if (sdtType === 'flush' && stepdownData) {
    stepdownData?.forEach(stepdown => {
      shapes.push({
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(stepdown?.timestamp),
        y0: 0,
        x1: new Date(stepdown?.timestamp),
        y1: 1,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 2,
          dash: 'dot'
        }
      });
    });
  } else if (sdtType === 'preSand' && preSandStepdownData) {
    preSandStepdownData?.forEach(stepdown => {
      shapes.push({
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(stepdown?.timestamp),
        y0: 0,
        x1: new Date(stepdown?.timestamp),
        y1: 1,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 2,
          dash: 'dot'
        }
      });
    });
  };
  layout.shapes = [...layout.shapes, ...shapes];

  // for each vertical line, add a text label
  const annotations = [];
  if (sdtType === 'flush' && stepdownData?.length > 0) {
    stepdownData.forEach((stepdown,i) => {
      annotations.push({
        x: new Date(stepdown?.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Step ${i+1}`,
        textangle: '-90',
        showarrow: false,
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
      });
    });
  } else if (sdtType === 'preSand' && preSandStepdownData?.length > 0) {
    preSandStepdownData.forEach((stepdown,i) => {
      annotations.push({
        x: new Date(stepdown?.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Step ${i+1}`,
        textangle: '-90',
        showarrow: false,
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
      });
    });
  };
  layout.annotations = [...layout.annotations, ...annotations];

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
      onClick={(e) => editMode ? handlePlotClick(e) : {}}
    />
  );

  
  // FUNCTIONS
  function handlePlotClick(e) {
    try {
      if (!editMode) return;

      if (sdtType === 'flush') {
        
        // check if a point exists at the clicked location
        const index = e.points.find(point => point.data.name === 'Stepdown Rate')?.pointIndex;
        if (index >= 0) { // existing point found, remove it
          let newStepdownData = stepdownData.filter((stepdown, i) => i !== index);
          newStepdownData.sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp));
          if (!newStepdownData?.length > 0) return;
          setStepdownData(newStepdownData);
        } else {
          const newIndex = e.points?.find(point => point.data.name === 'Rate (m3/min)')?.pointIndex;
  
          const newStep = stageData?.[newIndex];
  
          let newStepdownData = [...stepdownData, newStep];
          newStepdownData.sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp));
          if (!newStepdownData?.length > 0) return;

          setStepdownData(newStepdownData);
        };

        setRecalculate(true);

      } else if (sdtType === 'preSand') {
        // check if a point exists at the clicked location
        const index = e.points.find(point => point.data.name === 'Stepdown Rate')?.pointIndex;
        if (index >= 0) { // existing point found, remove it
          let newStepdownData = preSandStepdownData.filter((_, i) => i !== index);
          newStepdownData.sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp));
          if (!newStepdownData?.length > 0) return;
          setPreSandStepdownData(newStepdownData);
        } else {
          const newIndex = e.points?.find(point => point.data.name === 'Rate (m3/min)')?.pointIndex;
  
          const newStep = stageData?.[newIndex];
  
          let newStepdownData = [...preSandStepdownData, newStep];
          newStepdownData.sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp));
          if (!newStepdownData?.length > 0) return;

          setPreSandStepdownData(newStepdownData);
        };

        setRecalculate(true);

      };
  
      
    } catch (error) {
      console.log(error);
    };
  };

};

export const RTFOStepdownChartMemo = React.memo(RTFOStepdownChart);